// "Selfie search and face data from 1 November 2026" at /face-data-changes-2026 (English, the source of truth).
// Linked from the face-data campaign email (photohawk-serverless ops/campaigns/face-data-2026-11.json).
//
// Facts (keep in sync): photohawk-serverless docs/face-data-retention.md and src/layers/lib/src/faceRetention.js,
// and the public policy src/content/legal/biometric.en.ts. Wording rules, as for the policy: the law asks for a
// fixed, published limit and deletion once face data is no longer needed; 12 months is Photohawk's own limit
// inside those rules, so never claim that the law prescribes 12 months. No legal advice. Never assume the reader's
// role (no "photographer" / "organiser").

export interface FaceDataCopy {
  seo: { title: string; description: string };
  hero: { badge: string; title: string; titleHighlight: string; subtitle: string; ctaPolicy: string; ctaHow: string };
  /** The animated timeline. */
  timeline: {
    title: string;
    intro: string;
    label: string;
    caption: string;
    steps: { id: "upload" | "search" | "delete" | "carry"; when: string; title: string; body: string }[];
    /** Short labels inside the animation (keep them short: they sit in a 390px-wide figure). */
    scene: { selfie: string; upload: string; search: string; months: string; deleted: string; carry: string; match: string };
  };
  /** The "face template dissolving" illustration. */
  dissolve: {
    title: string;
    intro: string;
    label: string;
    caption: string;
    scene: { photo: string; template: string; bib: string; sale: string; gone: string };
  };
  november: { title: string; intro: string; points: { id: string; title: string; body: string }[] };
  unchanged: { title: string; intro: string; items: { id: string; text: string }[] };
  buyers: { title: string; intro: string; items: { id: string; text: string }[]; tip: string };
  why: { title: string; lead: string; paragraphs: string[]; laws: { id: string; name: string; text: string }[]; ourChoice: string; note: string };
  faq: { title: string; items: { id: string; q: string; a: string }[] };
  /** `allProducts`: the link to /face-data-policy (Photohawk Studio and Photohawk Family). */
  cta: { title: string; body: string; policy: string; contact: string; allProducts: string };
  pauseLabel: string;
  playLabel: string;
}

const en: FaceDataCopy = {
  seo: {
    title: "Selfie search and face data from 1 November | Photohawk",
    description:
      "From 1 November 2026, the face data behind selfie search is deleted automatically 12 months after each photo is uploaded. Photos, sales, downloads and bib search are not affected. Here's how it works and why.",
  },
  hero: {
    badge: "From 1 November 2026",
    title: "Selfie search and face data:",
    titleHighlight: "what's changing",
    subtitle:
      "From 1 November, the face data that makes selfie search work is deleted automatically 12 months after each photo is uploaded. Your photos stay exactly where they are. Privacy laws require a fixed limit like this, so it isn't something we can leave out.",
    ctaPolicy: "Read the face-data policy",
    ctaHow: "See how it works",
  },
  timeline: {
    title: "How it works",
    intro: "Every photo follows the same simple path. The clock is per photo: it starts when that photo is uploaded.",
    label: "Animated timeline: a photo is uploaded, selfie search finds it for 12 months, its face data is then deleted, and the photo carries on.",
    caption:
      "A photo is uploaded, selfie search finds it for 12 months, then its face data is deleted automatically. The photo itself, its sales and bib search carry on.",
    steps: [
      { id: "upload", when: "Day 1", title: "A photo is uploaded", body: "Faces in it are turned into face data, so buyers can find it with a selfie." },
      { id: "search", when: "For 12 months", title: "Selfie search finds it", body: "Buyers take a selfie and see every photo they're in, just like today." },
      { id: "delete", when: "At 12 months", title: "Its face data is deleted", body: "Automatically and permanently. The photo is never face-indexed again." },
      { id: "carry", when: "After that", title: "The photo carries on", body: "Buyers can still find it by bib number, time or browsing, and buy and download it." },
    ],
    scene: {
      selfie: "Selfie",
      upload: "Uploaded",
      search: "Selfie search",
      months: "12 months",
      deleted: "Face data deleted",
      carry: "Photo stays",
      match: "Match",
    },
  },
  dissolve: {
    title: "Face data isn't the photo",
    intro:
      "Face data is a set of numbers describing the faces in a photo: a face template. It's only used to match a buyer's selfie. Deleting it removes the template and nothing else.",
    label: "Animated illustration: a photo with its face template beside it; the template dissolves while the photo, its bib number and its sale stay.",
    caption: "The face template dissolves. The photo, its bib number and its sale don't change.",
    scene: { photo: "Photo", template: "Face template", bib: "Bib 1234", sale: "Sold", gone: "Deleted" },
  },
  november: {
    title: "What happens on 1 November",
    intro: "Photos uploaded more than 12 months ago are already past the limit, so they're dealt with first.",
    points: [
      {
        id: "backlog",
        title: "Older photos lose their face data first",
        body: "Face data for photos uploaded more than 12 months ago is deleted from 1 November. We work through them in batches, so it can take a few days to reach every gallery. From 1 November, no new face data is created for those photos.",
      },
      {
        id: "daily",
        title: "Then a daily check",
        body: "After that, an automated job runs every day and deletes each photo's face data as it reaches 12 months. Nothing to set up and nothing to remember.",
      },
      {
        id: "ended",
        title: "When a whole gallery is past 12 months",
        body: "Selfie search ends for that gallery. Buyers who try it see a short note that points them to bib search and browsing instead.",
      },
      {
        id: "off",
        title: "Closed galleries, or selfie search switched off",
        body: "Face data is deleted 30 days after a gallery is closed or its selfie search is switched off. For galleries already closed or switched off before 1 November, those 30 days start on 1 November.",
      },
    ],
  },
  unchanged: {
    title: "What doesn't change",
    intro: "Deleting face data never deletes a photo. Everything else carries on as it is today:",
    items: [
      { id: "photos", text: "Your photos and galleries" },
      { id: "sales", text: "Sales, orders and your earnings" },
      { id: "downloads", text: "Downloads, including free-download galleries" },
      { id: "bib", text: "Bib and number search, and text search" },
      { id: "time", text: "Time search and browsing" },
      { id: "recent", text: "Selfie search on photos uploaded in the last 12 months" },
    ],
  },
  buyers: {
    title: "What your buyers will notice",
    intro: "Most buyers look for their photos in the days and weeks after an event, so most won't notice anything.",
    items: [
      { id: "older", text: "Selfie search won't find photos uploaded more than 12 months ago." },
      { id: "other", text: "They can still find those photos by bib number, time or browsing the gallery, and buy and download them as usual." },
      { id: "note", text: "In a gallery where every photo is older than 12 months, selfie search shows a short note pointing them to the other ways to search." },
      { id: "selfies", text: "Selfies used to search are still never stored, as today." },
    ],
    tip: "A good habit: remind buyers to search soon after each event, in your event emails or social posts.",
  },
  why: {
    title: "Why this is happening",
    lead: "We don't have much control over this. It comes from privacy law, not from a decision to cut back selfie search.",
    paragraphs: [
      "Face templates are biometric data, which privacy laws treat as some of the most sensitive personal data there is. Those laws say it must have a fixed, published retention limit, be kept no longer than it's needed, and then be deleted automatically. Keeping face data indefinitely isn't an option for anyone who offers face search.",
      "Photohawk galleries are used all over the world, so one rule has to work under all of these laws at once:",
    ],
    laws: [
      { id: "gdpr", name: "GDPR and UK GDPR", text: "Biometric data used to identify people is a special category of personal data, and the storage-limitation principle says it must be kept no longer than necessary." },
      { id: "bipa", name: "Illinois (BIPA)", text: "Requires a public written retention schedule and destruction of biometric data once its purpose is met, with fixed deadlines." },
      { id: "cubi", name: "Texas (CUBI)", text: "Biometric identifiers must be destroyed within a reasonable time, and no later than a year after the purpose for collecting them ends." },
      { id: "co", name: "Colorado", text: "Biometric data must be deleted once its purpose is met, with fixed outer deadlines and a yearly review." },
      { id: "wa", name: "Washington", text: "Biometric identifiers may be kept no longer than reasonably necessary to provide the service." },
    ],
    ourChoice:
      "The one thing we could choose was the length. We chose 12 months: long enough to cover the time when buyers look for their photos, and within the deadlines these laws set.",
    note: "This page explains how Photohawk handles face data. It isn't legal advice.",
  },
  faq: {
    title: "Questions",
    items: [
      { id: "photos", q: "Will any of my photos be deleted?", a: "No. Only the face data used for selfie search is deleted. Photos, galleries, sales, orders and downloads are not affected." },
      { id: "action", q: "Do I need to do anything?", a: "No. It all happens automatically. If you like, remind buyers to search soon after each event." },
      { id: "longer", q: "Can I keep face data for longer, or opt my galleries out?", a: "No. The 12-month limit applies to every gallery on Photohawk, worldwide, and we can't make exceptions: a fixed limit that applies to everyone is exactly what these laws ask for." },
      { id: "sooner", q: "Can face data be deleted sooner?", a: "Yes. Switch off selfie search for a gallery, or close it, and its face data is deleted 30 days later. Switch it back on within those 30 days and that deletion is cancelled." },
      { id: "request", q: "What if someone asks for their face data to be deleted?", a: "Anyone can ask at any time, in the gallery (selfie search, then \"Make a request\") or by emailing support@photohawk.com. Their face data is deleted within 30 days, and you can also do it straight away from the gallery's selfie search settings." },
      { id: "find", q: "How will buyers find older photos?", a: "By bib or number search, time search or browsing the gallery, exactly as today. Only selfie search stops finding them." },
      { id: "selfies", q: "Are buyers' selfies stored?", a: "No. A selfie is used only for that one search and then discarded, as it is today." },
      { id: "plan", q: "Does this depend on my plan?", a: "No. It's the same for every plan and every gallery." },
    ],
  },
  cta: {
    title: "The full details",
    body: "Our face-data policy sets out exactly what face data we keep, for how long, and how it's deleted. Questions? Email us and a real person will reply.",
    policy: "Read the face-data policy",
    contact: "Email support@photohawk.com",
    allProducts: "Face data policy for Photohawk Studio and the Photohawk app",
  },
  pauseLabel: "Pause animation",
  playLabel: "Play animation",
};

export default en;
